<template>
  <div
    class="d-flex justify-lg-space-between mb-4"
    style="width: 100%"
  >
    <div
      class="mr-4"
      style="position: relative; width: 100%"
    >
      <div
        v-if="inputHelpIsActive"
        class="d-flex"
      >
        <v-text-field
          v-model="latString"
          variant="filled"
          style="width: 45%; height: 100%"
          class="mr-2"
          :label="t('lat')"
          suffix="°"
          :rules="[isValidLatitude]"
          :error="!insideBounds"
          data-testid="lat-input"
          @input="onChangeSingle"
          @focus="handleCoordinateClicked"
        />
        <v-text-field
          v-model="lonString"
          variant="filled"
          style="width: 45%; height: 100%"
          class="ml-2"
          :label="t('lon')"
          suffix="°"
          :rules="[isValidLongitude]"
          :error="!insideBounds"
          data-testid="lon-input"
          @input="onChangeSingle"
          @focus="handleCoordinateClicked"
        />
      </div>
      <v-text-field
        v-if="!inputHelpIsActive"
        v-model="latLonString"
        style="width: 100%; height: 100%"
        class="mr-4"
        full-width
        variant="filled"
        :label="t('lat') + ', ' + t('lon')"
        :rules="[isValidLatLon]"
        data-testid="lat-lon-input"
        @input="onChangeCombined"
        @focus="handleCoordinateClicked"
      />
    </div>
    <v-tooltip
      :text="t('raster.tooltip.reset_coordinate')"
      bottom
      :open-delay="200"
    >
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          rounded
          class="tool-button"
          :elevation="0"
          :data-testid="`remove-coordinate-for-id-${id}`"
          @click="timeseriesStore.removeCoordsForID(id)"
        >
          <v-icon>mdi-delete-outline</v-icon>
        </v-btn>
      </template>
    </v-tooltip>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted } from 'vue';
import { LatLng } from 'leaflet';
import TimeseriesStore from '@/stores/timeseries/timeseries-module';
import RasterDatasetStore from '@/stores/dataset/raster-dataset-module';
import { isCoordinate } from '@/stores/timeseries/timeseries-model';
import { useI18n } from "vue-i18n";

export default defineComponent ({
  name: 'LatLonChoiceField',
  props: {
    inputHelpIsActive: { type: Boolean, required: true },
    coords: { type: Object as () => LatLng | null, required: true },
    id: { type: String, required: true },
  },
  setup(props) {
    const { t } = useI18n();

    const timeseriesStore = TimeseriesStore;
    const rasterDatasetStore = RasterDatasetStore;

    // set start strings to non-empty to make sure that the text fields have an error state in the beginning
    const latString = ref(' ');
    const lonString = ref(' ');
    const latLonString = ref(' ');

    const insideBounds = ref(true);

    const setStringsFromCoords = () => {
      if (!insideBounds.value) {
        // don't override wrong values in the input field
        return;
      }
      if (props.coords === null) {
        latString.value = '';
        lonString.value = '';
        latLonString.value = '';
      } else {
        latString.value = props.coords.lat.toString();
        lonString.value = props.coords.lng.toString();
        latLonString.value = `${props.coords.lat.toString()}, ${props.coords.lng.toString()}`;
      }
    };

    watch(() => props.coords, () => {
      setStringsFromCoords();
    });

    onMounted(() => {
      setStringsFromCoords();
    });

    const handleCoordinateClicked = () => {
      timeseriesStore.setHighlightedCoordID(props.id);
    };

    const submitLatLonToStore = (latText: string, lonText: string) => {
      const lat = parseFloat(latText);
      const lon = parseFloat(lonText);
      if (isNaN(lat) || isNaN(lon)) return;
      const coords = new LatLng(lat, lon);
      if (rasterDatasetStore.isCoordInsideBounds(coords)) {
        insideBounds.value = true;
        timeseriesStore.updateCoordsForID({ coords, id: props.id });
      } else {
        timeseriesStore.resetCoordsForID(props.id);
        insideBounds.value = false;
      }
    };

    const onChangeSingle = () => {
      if (latString.value.endsWith('.') || lonString.value.endsWith('.')) return;
      submitLatLonToStore(latString.value, lonString.value);
    };

    const onChangeCombined = () => {
      if (latLonString.value.endsWith('.')) return;
      const arr = latLonString.value.split(',');
      let lat = '';
      let lon = '';
      if (arr.length >= 1) lat = arr[0];
      if (arr.length >= 2) lon = arr[1];
      submitLatLonToStore(lat, lon);
    };

    // adhoc function used for v-text-field 'rules' prop
    const isValidLatitude = (val: any): string | boolean => {
      if (!isCoordinate(val)) return t('raster.invalid_latitude').toString();
      if (!rasterDatasetStore.isLatInsideBounds(val)) return t('raster.latitude_out_of_bounds').toString();
      if (!insideBounds.value) return t('raster.coordinate_out_of_bounds').toString();
      return true;
    };

    // adhoc function used for v-text-field 'rules' prop
    const isValidLongitude = (val: any): string | boolean => {
      if (!isCoordinate(val)) return t('raster.invalid_longitude').toString();
      if (!rasterDatasetStore.isLonInsideBounds(val)) return t('raster.longitude_out_of_bounds').toString();
      if (!insideBounds.value) return t('raster.coordinate_out_of_bounds').toString();
      return true;
    };

    // adhoc function used for v-text-field 'rules' prop
    const isValidLatLon = (val: any): string | boolean => {
      if (typeof val !== 'string') return t('raster.invalid_coordinate').toString();
      const xSplit = val.split(',');
      if (xSplit.length !== 2) return t('raster.invalid_coordinate').toString();
      const validationLat = isValidLatitude(xSplit[0]);
      if (validationLat !== true) return validationLat;
      const validationLon = isValidLongitude(xSplit[1]);
      if (validationLon !== true) return validationLon;
      return true;
    };

    return {
      t,
      latString,
      lonString,
      latLonString,
      insideBounds,
      handleCoordinateClicked,
      onChangeSingle,
      onChangeCombined,
      isValidLatitude,
      isValidLongitude,
      isValidLatLon,
      timeseriesStore,
      rasterDatasetStore,
    };
  },
});
</script>

<style scoped>
.error-message {
  width: 100%;
  position: absolute;
  margin-top: 60px;
  font-size: 12px;
  color: rgb(var(--v-error-base));
  transition: margin-top 0.2s ease-out;
}

.shift-down {
  margin-top: 75px;
}

.tool-button {
  margin-top: 10px;
  margin-right: 16px;
}
</style>
